import { AxiosError } from 'axios';
import { f7 } from 'framework7-react';
import { useState } from 'react';
import { customerInfoApi } from '@/api/customerApi';
import { paymentMethodChangeApi } from '@/api/warrantyApi';
import { store } from '@/config/store';
import { useToast } from '@/hooks/useToast';
import { PaymentMethodChangeParams } from '@/types/api/warrantyApi';
import { dispatchBasicInfo, storeDispatch } from '@/utils/store';

const NEXT_WARRANTY_BRANCH_INCREMENT = 1;

export const refreshPaymentMethodCustomerInfo = async (
  params: PaymentMethodChangeParams,
): Promise<void> => {
  const customerId = params.m_customer_id;
  // 保証の情報は所属法人で取得する。ショッピング先の切り替えは混入させない。
  const { data: customerInfo, requestedAffiliateId } = await customerInfoApi(
    { m_customer_id: customerId },
    { applySelectedTenant: false },
  );
  if (!customerInfo.success || customerInfo.customer?.id !== customerId)
    throw new Error('顧客情報の取得に失敗しました');
  // 応答待ち中に別顧客へ切り替わったら、その顧客のストアを上書きしない。
  if (store.state.authInfo.m_customer_id !== customerId) return;
  const selectedCarId = store.state.carItemInfo.m_car_id;
  // 顧客詳細と取得済み法人IDを同じ応答から更新する。
  await dispatchBasicInfo(
    customerInfo,
    requestedAffiliateId,
    (candidateCar) => candidateCar.m_car_id === selectedCarId,
  );

  if (params.warranty_no) {
    const car = customerInfo.cars?.find(
      (candidateCar) => candidateCar.m_car_id === selectedCarId,
    );
    if (car) {
      const restoredCurrentWarranty = car.warranties?.find(
        (warranty) =>
          warranty.warranty_no === params.warranty_no &&
          (params.branch_no === undefined ||
            warranty.branch_no === Number(params.branch_no)),
      );
      await Promise.all([
        storeDispatch(
          'setCurrentWarranty',
          'currentWarranty',
          restoredCurrentWarranty ?? car.warranties?.[0],
        ),
        storeDispatch(
          'setNextWarranty',
          'nextWarranty',
          car.next_warranty?.find(
            (nextWarranty) =>
              nextWarranty.warranty_no === params.warranty_no &&
              (params.branch_no === undefined ||
                nextWarranty.branch_no ===
                  Number(params.branch_no) + NEXT_WARRANTY_BRANCH_INCREMENT),
          ) ?? car.next_warranty?.[0],
        ),
      ]);
    }
  }
};

export const usePaymentMethodChange = () => {
  const [error, setError] = useState<AxiosError>();
  const { openToast } = useToast();
  const changePaymentMethod = async (
    params: PaymentMethodChangeParams,
  ): Promise<boolean> => {
    try {
      f7.preloader.show();
      const res = await paymentMethodChangeApi(params);
      if (!res.data.success) {
        openToast(
          res.data.error?.message ?? '決済方法の変更に失敗しました',
          'toast-failed',
        );
        return false;
      }

      await refreshPaymentMethodCustomerInfo(params);
      openToast('決済方法を変更しました。');
      return true;
    } catch (err) {
      setError(err as AxiosError);
      openToast('決済方法の変更に失敗しました', 'toast-failed');
      return false;
    } finally {
      f7.preloader.hide();
    }
  };

  return {
    changePaymentMethod,
    error,
  };
};
